// Copyright ©️ 2026 eVolve MEP, LLC

import type { ShipmentItem, WorkOrder, WorkOrderItem, WorkOrderStateName, MappedSpool } from '@evolvemep/api-ts';

import type { StateNameFor } from 'components/Mantine/StatusEditor/types';

/** Used to format the full name of a Work Order */
export const getDisplayNameWO = (
  workOrder:
    | Pick<WorkOrder, 'name' | 'identifier'>
    | {
        workOrderName: string;
        workOrderIdentifier: string;
      },
) =>
  'workOrderName' in workOrder
    ? `${workOrder.workOrderName}: ${workOrder.workOrderIdentifier}`
    : `${workOrder.name}: ${workOrder.identifier}`;

const releasedStates: WorkOrderStateName[] = ['Not Started', 'In Progress'];

const checkState = (
  workOrder: Pick<WorkOrder, 'workOrderStateName'> | WorkOrderStateName,
  states: WorkOrderStateName[],
) => states.includes(typeof workOrder === 'object' ? workOrder.workOrderStateName : workOrder);

export const isReleased = (workOrder: Pick<WorkOrder, 'workOrderStateName'> | WorkOrderStateName) =>
  checkState(workOrder, releasedStates);

export const hasEverBeenReleased = (workOrder: Pick<WorkOrder, 'workOrderStateName'> | WorkOrderStateName) =>
  checkState(workOrder, [...releasedStates, 'Blocked', 'Completed']);

/** An "item" that carries tasks or fabrication elements. */
export type ItemWithStatus = Pick<
  WorkOrderItem | ShipmentItem | MappedSpool,
  | 'hasShopTask'
  | 'hasFabricationElements'
  | 'anyTaskStarted'
  | 'allTasksCompleted'
  | 'anyFabricationElementsMarkedDone'
  | 'allFabricationElementsMarkedDone'
>;

/** The Task-flavored state of a work order or shipment item, to be rendered on a status badge/point. */
export const getTaskStatusFromItem = (item: ItemWithStatus): StateNameFor<'Task'> | null => {
  if (!item.hasShopTask && !item.hasFabricationElements) return null;

  if (
    (!item.hasShopTask || item.allTasksCompleted) &&
    (!item.hasFabricationElements || item.allFabricationElementsMarkedDone)
  ) {
    return 'Completed';
  }
  return item.anyTaskStarted || item.anyFabricationElementsMarkedDone ? 'Started' : 'Not Started';
};

export const getRemainingItemsToShip = (wo: WorkOrderItem | ShipmentItem) =>
  wo.totalQuantity - wo.totalAllocatedQuantity;
